import { useEffect, useCallback } from 'react';

/**
 * Hook für Clipboard-Paste-Funktion (Cmd+V oder Ctrl+V)
 * Erlaubt das Einfügen von Screenshots direkt aus der Zwischenablage
 */
export interface UseClipboardPasteOptions {
  /** Callback wenn Bilder aus Clipboard gepastet werden */
  onPaste?: (files: File[]) => void;
  /** Target element ref - wenn nicht gesetzt wird document genutzt */
  targetRef?: React.RefObject<HTMLElement>;
  /** Erlaubte MIME-Typen (default: alle image types) */
  allowedMimeTypes?: string[];
}

/**
 * Hook für das Handhaben von Clipboard-Paste-Events
 * Extrahiert Bilder aus der Zwischenablage und konvertiert zu Files
 */
export function useClipboardPaste(
  options: UseClipboardPasteOptions = {}
): { isSupported: boolean } {
  const {
    onPaste,
    targetRef,
    allowedMimeTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
  } = options;

  // Feature Detection: Clipboard API Unterstützung
  const isSupported =
    !!(
      navigator &&
      navigator.clipboard &&
      navigator.clipboard.read
    ) ||
    typeof ClipboardEvent !== 'undefined';

  const handlePaste = useCallback(
    async (event: ClipboardEvent) => {
      if (!onPaste) return;

      try {
        // Versuche moderne Clipboard API (Chrom/Firefox)
        if (navigator.clipboard && navigator.clipboard.read) {
          const items = await navigator.clipboard.read();
          const files: File[] = [];

          for (const item of items) {
            // Prüfe alle verfügbaren MIME-Typen
            for (const mimeType of item.types) {
              if (allowedMimeTypes.some((allowed) => mimeType.includes(allowed))) {
                const blob = await item.getType(mimeType);
                const file = new File([blob], `pasted_image_${Date.now()}.${mimeType.split('/')[1]}`, {
                  type: mimeType,
                });
                files.push(file);
                break; // Nur ein Format pro Item
              }
            }
          }

          if (files.length > 0) {
            event.preventDefault();
            onPaste(files);
          }
        } else {
          // Fallback für ältere Browser: DataTransfer API (weniger zuverlässig)
          const items = event.clipboardData?.items;
          if (!items) return;

          const files: File[] = [];
          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (allowedMimeTypes.includes(item.type)) {
              const blob = item.getAsFile();
              if (blob) {
                files.push(
                  new File([blob], `pasted_image_${Date.now()}_${i}.${item.type.split('/')[1]}`, {
                    type: item.type,
                  })
                );
              }
            }
          }

          if (files.length > 0) {
            event.preventDefault();
            onPaste(files);
          }
        }
      } catch (error) {
        console.error('[useClipboardPaste] Fehler beim Verarbeiten von Clipboard-Daten:', error);
        // Fehler ignorieren - nicht kritisch
      }
    },
    [onPaste, allowedMimeTypes]
  );

  useEffect(() => {
    if (!isSupported) return;

    const target = targetRef?.current || document;
    target.addEventListener('paste', handlePaste as unknown as EventListener);

    return () => {
      target.removeEventListener('paste', handlePaste as unknown as EventListener);
    };
  }, [handlePaste, isSupported, targetRef]);

  return { isSupported };
}
